/* Nordri collection filter: filters, sorts and pages the products already on the page.
   Ticked submenus show products in any of them; gift ticks narrow that further; price and
   sort apply on top. Nothing is fetched, so it is instant. */
(() => {
  if (customElements.get('nordri-filter')) return;

  class NordriFilter extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-grid]');
      if (!this.grid || this.ready) return;
      this.ready = true;
      this.items = [...this.grid.children];
      this.per = parseInt(this.dataset.per, 10) || 12;
      this.page = 1;
      this.sort = 'featured';
      this.panel = this.querySelector('[data-panel]');
      this.shade = this.querySelector('[data-shade]');
      this.toggleBtn = this.querySelector('[data-toggle]');
      this.pmin = this.querySelector('[data-pmin]');
      this.pmax = this.querySelector('[data-pmax]');
      this.subs = [...this.querySelectorAll('[data-sub]')];
      this.gifts = [...this.querySelectorAll('[data-gift]')];

      const prices = this.items.flatMap((li) => [li.dataset.price, li.dataset.priceMax]).map((n) => parseInt(n, 10) / 100).filter((n) => !isNaN(n));
      if (prices.length) {
        const fmt = (n) => this.money(n);
        if (this.pmin) this.pmin.placeholder = fmt(Math.min(...prices));
        if (this.pmax) this.pmax.placeholder = fmt(Math.max(...prices));
      }

      this.toggleBtn.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
      this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.shade.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.panel.hidden) {
          this.close();
          this.toggleBtn.focus();
        }
      });
      document.addEventListener('click', (e) => {
        if (this.panel.hidden || window.matchMedia('(max-width: 749px)').matches) return;
        if (!this.querySelector('.nf__bar').contains(e.target)) this.close();
      });

      this.querySelectorAll('.nf__acc-btn').forEach((b) =>
        b.addEventListener('click', () => {
          const acc = b.closest('.nf__acc');
          const on = acc.classList.toggle('is-open');
          b.setAttribute('aria-expanded', on);
        })
      );

      this.querySelectorAll('input[type=checkbox], input[type=radio]').forEach((i) =>
        i.addEventListener('change', () => {
          if (i.type === 'radio') this.sort = i.value;
          this.page = 1;
          this.apply();
        })
      );
      [this.pmin, this.pmax].forEach((i) => i && i.addEventListener('input', () => ((this.page = 1), this.apply())));
      this.querySelector('[data-clear]').addEventListener('click', () => this.clear());
      this.querySelector('[data-chips]').addEventListener('click', (e) => {
        const b = e.target.closest('[data-rm]');
        if (!b) return;
        const [kind, key] = b.dataset.rm.split(':');
        if (kind === 'sub') this.subs.find((i) => i.dataset.sub === key).checked = false;
        else if (kind === 'gift') this.gifts.find((i) => i.dataset.gift === key).checked = false;
        else {
          if (this.pmin) this.pmin.value = '';
          if (this.pmax) this.pmax.value = '';
        }
        this.page = 1;
        this.apply();
      });
      this.querySelectorAll('[data-pager]').forEach((p) =>
        p.addEventListener('click', (e) => {
          const b = e.target.closest('[data-p]');
          if (!b || b.disabled) return;
          this.page = parseInt(b.dataset.p, 10);
          this.apply(true);
        })
      );

      this.apply();
    }

    money(n) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
          maximumFractionDigits: 0,
        }).format(n);
      } catch (e) {
        return String(Math.round(n));
      }
    }

    open() {
      this.panel.hidden = false;
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      if (window.matchMedia('(max-width: 749px)').matches) {
        this.shade.hidden = false;
        document.documentElement.style.overflow = 'hidden';
      }
      this.panel.querySelector('[data-close]')?.focus({ preventScroll: true });
    }

    close() {
      this.panel.hidden = true;
      this.shade.hidden = true;
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
    }

    clear() {
      this.subs.forEach((i) => (i.checked = true));
      this.gifts.forEach((i) => (i.checked = false));
      if (this.pmin) this.pmin.value = '';
      if (this.pmax) this.pmax.value = '';
      const f = this.querySelector('input[type=radio][value=featured]');
      if (f) f.checked = true;
      this.sort = 'featured';
      this.page = 1;
      this.apply();
    }

    matches(li) {
      const c = li.dataset.c;
      const sub = this.subs.filter((i) => i.checked);
      if (this.subs.length && sub.length < this.subs.length && !sub.some((i) => c.includes(`|${i.dataset.sub}|`))) return false;
      const gift = this.gifts.filter((i) => i.checked);
      if (gift.length && !gift.some((i) => c.includes(`|${i.dataset.gift}|`))) return false;
      const price = parseInt(li.dataset.price, 10) / 100;
      const mn = parseFloat(this.pmin?.value);
      const mx = parseFloat(this.pmax?.value);
      if (!isNaN(mn) && price < mn) return false;
      if (!isNaN(mx) && price > mx) return false;
      return true;
    }

    apply(scroll) {
      const by = {
        featured: (a, b) => a.dataset.i - b.dataset.i,
        az: (a, b) => a.dataset.title.localeCompare(b.dataset.title),
        za: (a, b) => b.dataset.title.localeCompare(a.dataset.title),
        lh: (a, b) => a.dataset.price - b.dataset.price || a.dataset.i - b.dataset.i,
        hl: (a, b) => b.dataset.price - a.dataset.price || a.dataset.i - b.dataset.i,
      };
      const ok = this.items.filter((li) => this.matches(li)).sort(by[this.sort] || by.featured);
      const pages = Math.max(1, Math.ceil(ok.length / this.per));
      this.page = Math.min(this.page, pages);
      const from = (this.page - 1) * this.per;
      const show = new Set(ok.slice(from, from + this.per));
      const frag = document.createDocumentFragment();
      ok.forEach((li) => frag.appendChild(li));
      this.items.filter((li) => !ok.includes(li)).forEach((li) => frag.appendChild(li));
      this.grid.appendChild(frag);
      this.items.forEach((li) => (li.hidden = !show.has(li)));

      this.querySelector('[data-empty]').hidden = ok.length > 0;
      this.querySelector('[data-count]').textContent = `${ok.length} ${ok.length === 1 ? 'piece' : 'pieces'}`;
      this.querySelectorAll('[data-n]').forEach((n) => (n.textContent = ok.length));
      this.chips();
      this.pager(pages);
      if (scroll) this.querySelector('.nf__bar').scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    chips() {
      const x = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      let html = '';
      this.subs.filter((i) => i.checked).forEach((i) => (html += `<button type="button" class="nf__chip" data-rm="sub:${i.dataset.sub}">${i.dataset.label}${x}</button>`));
      this.gifts.filter((i) => i.checked).forEach((i) => (html += `<button type="button" class="nf__chip nf__chip--gift" data-rm="gift:${i.dataset.gift}">${i.dataset.label}${x}</button>`));
      const mn = this.pmin?.value;
      const mx = this.pmax?.value;
      if (mn || mx) html += `<button type="button" class="nf__chip nf__chip--price" data-rm="price">${mn ? this.money(mn) : this.money(0)} – ${mx ? this.money(mx) : '∞'}${x}</button>`;
      this.querySelector('[data-chips]').innerHTML = html;

      const active =
        this.subs.filter((i) => !i.checked).length + this.gifts.filter((i) => i.checked).length + (mn || mx ? 1 : 0) + (this.sort !== 'featured' ? 1 : 0);
      const badge = this.querySelector('[data-badge]');
      badge.hidden = !active;
      badge.textContent = active;
    }

    pager(pages) {
      const nav = this.querySelector('[data-pager]');
      if (pages <= 1) {
        nav.innerHTML = '';
        return;
      }
      const cur = this.page;
      const nums = [...new Set([1, pages, cur - 1, cur, cur + 1])].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
      const ar = (d) => `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;
      let html = `<button type="button" class="ncpg__btn" data-p="${cur - 1}" ${cur === 1 ? 'disabled' : ''} aria-label="Previous page">${ar('M15 6l-6 6 6 6')}</button>`;
      let prev = 0;
      nums.forEach((n) => {
        if (n - prev > 1) html += '<span class="ncpg__gap">…</span>';
        html += `<button type="button" class="ncpg__btn" data-p="${n}" ${n === cur ? 'aria-current="page"' : ''}>${n}</button>`;
        prev = n;
      });
      html += `<button type="button" class="ncpg__btn" data-p="${cur + 1}" ${cur === pages ? 'disabled' : ''} aria-label="Next page">${ar('M9 6l6 6-6 6')}</button>`;
      nav.innerHTML = html;
    }
  }

  customElements.define('nordri-filter', NordriFilter);

  /* Page numbers for the collections list: the cards are all on the page, this shows one page of them. */
  class NordriPages extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.items = [...this.querySelectorAll('[data-item]')];
      this.per = parseInt(this.dataset.per, 10) || 12;
      this.page = 1;
      this.nav = this.querySelector('[data-pager]');
      this.nav.addEventListener('click', (e) => {
        const b = e.target.closest('[data-p]');
        if (!b || b.disabled) return;
        this.page = parseInt(b.dataset.p, 10);
        this.render(true);
      });
      this.render();
    }

    render(scroll) {
      const pages = Math.max(1, Math.ceil(this.items.length / this.per));
      const cur = (this.page = Math.min(this.page, pages));
      this.items.forEach((el, i) => (el.hidden = i < (cur - 1) * this.per || i >= cur * this.per));
      if (pages <= 1) {
        this.nav.innerHTML = '';
        return;
      }
      const nums = [...new Set([1, pages, cur - 1, cur, cur + 1])].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
      const ar = (d) => `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${d}"/></svg>`;
      let html = `<button type="button" class="ncpg__btn" data-p="${cur - 1}" ${cur === 1 ? 'disabled' : ''} aria-label="Previous page">${ar('M15 6l-6 6 6 6')}</button>`;
      let prev = 0;
      nums.forEach((n) => {
        if (n - prev > 1) html += '<span class="ncpg__gap">…</span>';
        html += `<button type="button" class="ncpg__btn" data-p="${n}" ${n === cur ? 'aria-current="page"' : ''}>${n}</button>`;
        prev = n;
      });
      html += `<button type="button" class="ncpg__btn" data-p="${cur + 1}" ${cur === pages ? 'disabled' : ''} aria-label="Next page">${ar('M9 6l6 6-6 6')}</button>`;
      this.nav.innerHTML = html;
      if (scroll) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  customElements.define('nordri-pages', NordriPages);
})();
